import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import {
  Badge,
  Banner,
  Button,
  ConfirmDialog,
  EmptyState,
  Page,
  Spinner,
  Table,
  VendorMark,
  type Column,
} from "../../components";
import { describeApiError, isApiError } from "../../api/errors";
import {
  useConnections,
  useRevokeConnection,
  useStartLuciBookAuthorization,
  useStartXeroAuthorization,
} from "../../api/hooks";
import { useToast } from "../../components/Toast";
import { formatDate, type Connection, type RevokeState } from "../../api/types";
import { oauthDeniedMessage, revokeAccessHolderName, vendorName } from "../../i18n/oauthCopy";
import { formatRelativeExpiry } from "../../i18n/relativeTime";
import { t, tDynamic } from "../../i18n/t";
import { saveDraft } from "../connect/newMigrationDraft";

/**
 * §B6.5 / v3-BL-2, redesigned as Connections.html: one table — System (22 px vendor mark + name),
 * Organisation, Status (pill, §6: Active → pass, Revoke failed → warn, Revoked → missing), Token expires,
 * Refresh expires, and compact row actions. The warn banner with the retry action covers the one case the
 * backend can still produce as retryable (§A6.9: Xero and LuciBook keep their blob so a retry is real).
 *
 * **Revocation is best-effort and is never shown as done until the vendor confirms it.** `revoke_state` is
 * the API's own derivation (§A6.10) from `revoked_at` plus the newest `connection.revoke*` audit row. A
 * permanent `revoke_failed` row (no stored credentials left to retry with) gets the terminal "Mark as
 * revoked" action and its own truthful note; a `revoked` row that still carries a `revoke_warning` shows it.
 *
 * **Package W2c**: this is also the landing target `_connections_return` sends the browser to after a QBO,
 * FreeAgent or Sage BC OAuth callback — `?connected=<system>` on success, `?error=oauth_state_invalid` or
 * `?error=oauth_denied` on failure — because none of the three has a Xero-style tenant-picker return page of
 * its own. The query string is turned into a toast once, on mount, then stripped with `navigate(..., {replace:
 * true})` so a refresh of `/connections` never replays it.
 */
/**
 * §B7.6's own literal-call convention (`tests/i18n.test.ts`'s "every key is referenced" check greps source for
 * a literal, double-quoted first argument to `t`, not a key computed through a lookup table): one call per
 * system below, not a map from `connected` to a key. `xero` is deliberately absent — its own callback
 * redirects to `/oauth/xero/return`, never here (`_return_to`, not `_connections_return`) — only QBO/
 * FreeAgent/Sage BC ever land on `?connected=<system>`.
 */
function connectedToast(system: string): string {
  if (system === "qbo") return t("connections.qbo_connected");
  if (system === "freeagent") return t("connections.freeagent_connected");
  if (system === "sage_bc") return t("connections.sagebc_connected");
  if (system === "lucibook") return t("connections.lucibook_oauth_connected");
  return t("connections.oauth_connected_generic", { system });
}

/**
 * XMG-AUTOBASIS: a row is titled by the ORGANISATION it connects (LuciBook org name / Xero tenant name), never by
 * a base URL or a sign-in email. The raw id is only the fallback while the name is still being filled in.
 */
export function connectionTitle(row: Connection): string {
  const named = (row.organisation_name ?? row.tenant_name ?? "").trim();
  if (named !== "") return named;
  // Not yet known: a name the person typed for the connection is better than an id, but a URL or an email is
  // exactly what made the cards unreadable, so those are never a title.
  const typed = row.display_name?.trim() ?? "";
  return typed !== "" && !/^https?:\/\//.test(typed) && !typed.includes("@") ? typed : row.org_id;
}

export function ConnectionsScreen(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  // "Show history": a reconnect supersedes the older row for the same organisation, so the list shows only the
  // latest row per (system, organisation) unless the person asks for the rest.
  const [showHistory, setShowHistory] = useState(false);
  const query = useConnections({ includeSuperseded: showHistory });
  const revoke = useRevokeConnection();
  const [pending, setPending] = useState<Connection | null>(null);
  // Round 7 (Opus verification review of round 6, LOW): "Mark as revoked" (the terminal action for a
  // `revoke_failed` row with no stored credentials left, below) used to fire `revoke.mutate` straight off
  // the click - no confirmation at all, unlike every other destructive action on this screen. Its own
  // pending state, separate from `pending` above (the "Revoke"/"Revoke again" dialog), because the two
  // actions need different copy: this one never contacts the vendor.
  const [pendingMarkRevoked, setPendingMarkRevoked] = useState<Connection | null>(null);

  // Names the server could not give yet are filled in the background after the list response (it never waits on a
  // vendor); ask once more a few seconds later so they appear without the person reloading. XMG-SEC C1: the same
  // background pass checks each live Xero row against Xero's `GET /connections` (Xero has no disconnect webhook),
  // so a live Xero row is a reason to ask once more too - a disconnect found there shows on that second answer.
  const unnamed = (query.data ?? []).some((row) => (row.organisation_name ?? row.tenant_name ?? "") === "");
  const liveXero = (query.data ?? []).some(
    (row) => row.system === "xero" && (row.revoke_state ?? "active") === "active",
  );
  const refetchSoon = unnamed || liveXero;
  const refetchOnce = useRef(false);
  useEffect(() => {
    if (!refetchSoon || refetchOnce.current) return undefined;
    refetchOnce.current = true;
    const timer = window.setTimeout(() => void query.refetch(), 4000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount, keyed on whether a second answer can differ
  }, [refetchSoon]);

  useEffect(() => {
    const connected = params.get("connected");
    const errorCode = params.get("error");
    if (connected !== null) {
      toast.push(connectedToast(connected));
    } else if (errorCode !== null) {
      // F3/F6: `system` names the vendor that declined (every denial redirect sets it now,
      // `connections.py::_denied_query`); `oauth_error` is the whitelisted raw vendor code.
      //
      // `oauth_connection_mismatch` (Opus review, 2026-09-24): LuciBook's callback carries its own
      // `?connection_id=` and `lucibook_oauth_callback` redirects here with this code when that param is
      // missing or does not match anything `list_connections()` returned — the one case this callback
      // refuses to guess an org for. A plain "try connecting again" is the right recovery: the mismatch
      // is transient (a stale/duplicate redirect), never something the operator can fix by choosing
      // differently.
      toast.push(
        errorCode === "oauth_denied"
          ? oauthDeniedMessage(params.get("system"), params.get("oauth_error"))
          : errorCode === "oauth_connection_mismatch"
            ? t("oauth.connection_mismatch")
            : // CB2 round 2 (Opus LOW findings): `vendor_unavailable`/`oauth_operator_mismatch`/`internal`
              // each get their own accurate toast now — `vendor_unavailable` in particular must never say
              // "link is no longer valid": the callback leaves the state UN-consumed on that outcome
              // specifically so a retry works (`connections.py::lucibook_oauth_callback`'s own table).
              errorCode === "vendor_unavailable"
              ? t("oauth.vendor_unavailable", { system: vendorName(params.get("system")) })
              : // Round 3, item 5 (LOW): a PERMANENT rejection (`PermanentConnectorError`) is split out of
                // `vendor_unavailable` above — that copy's "try again in a moment" reads as "this is a
                // blip", which is not true when the vendor was reached and said no.
                errorCode === "vendor_rejected"
                ? t("oauth.vendor_rejected", { system: vendorName(params.get("system")) })
                : errorCode === "oauth_operator_mismatch"
                  ? t("oauth.operator_mismatch")
                  : errorCode === "internal"
                    ? t("oauth.internal_error")
                    : t("oauth.state_invalid"),
        null,
        "error",
      );
    }
    if (connected !== null || errorCode !== null) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per mount/query-string change, not per render
  }, [params]);

  // Round 5 (connection-revoke truthfulness): the banner's promise that a retry makes a real vendor call is
  // true ONLY for a genuinely RETRYABLE `revoke_failed` row (`revoke_retryable`) - a PERMANENT failure or a
  // legacy null-blob `revoke_failed` row never reaches this banner, because retrying either cannot help at
  // all (a bare `revoke_state === "revoke_failed"` check used to show it for both alike). The banner names
  // the first such row's vendor and its retry repeats that row's action.
  const firstRetryable = (query.data ?? []).find(
    (row) => row.revoke_state === "revoke_failed" && row.revoke_retryable,
  );

  return (
    <Page
      eyebrow={t("operator.eyebrow")}
      title={t("connections.title")}
      caption={t("connections.caption")}
      actions={
        <Button variant="primary" onClick={() => navigate("/connections/add")}>
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          {t("connections.add")}
        </Button>
      }
    >
      {firstRetryable === undefined ? null : (
        <Banner
          tone="warn"
          title={t("revoke.revoke_failed", { vendor: vendorName(firstRetryable.system) })}
          action={
            <Button size="compact" className="banner__action" onClick={() => setPending(firstRetryable)}>
              {t("connections.revoke_again")}
            </Button>
          }
        >
          {t("connections.revoke_banner_body", { vendor: vendorName(firstRetryable.system) })}
        </Banner>
      )}
      <label className="chk">
        <input
          type="checkbox"
          checked={showHistory}
          onChange={(event) => setShowHistory(event.target.checked)}
        />
        {t("connections.show_history")}
      </label>
      {query.isPending ? (
        <Spinner />
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState title={t("connections.title")} body={t("connections.empty")} />
      ) : (
        <section className="card card--flush">
          <Table
            bare
            caption={t("connections.caption")}
            columns={connectionColumns({
              rows: query.data ?? [],
              onRevoke: setPending,
              onMarkRevoked: setPendingMarkRevoked,
            })}
            rows={query.data ?? []}
            rowKey={(row) => row.id}
          />
        </section>
      )}
      <ConfirmDialog
        open={pending !== null}
        destructive
        title={t("connections.revoke_confirm_title")}
        confirmLabel={t("connections.revoke")}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          const target = pending;
          setPending(null);
          if (target !== null) revoke.mutate(target.id);
        }}
      >
        {/* Round 4 (Opus verification review, LOW), extended round 7 (Opus verification review of round
            6, LOW): `revoke_confirm_body`'s "kept so this can be tried again" clause is only true for
            Xero and LuciBook - the two systems `revoke_connection_task`/`stage_lock` ever actually call
            out to and retry after a refresh (§A6.9). Every OTHER system (QBO/FreeAgent/Sage BC/HMRC/
            `fake`) has no revoke path built at all - no vendor call is ever made, and the blob is always
            deleted unconditionally - so it gets its OWN accurate copy instead of a promise that never
            applies to it. */}
        <p>
          {pending !== null && (pending.system === "xero" || pending.system === "lucibook")
            ? t("connections.revoke_confirm_body", { name: connectionTitle(pending) })
            : t("connections.revoke_confirm_body_no_vendor_call", {
                name: pending === null ? "" : connectionTitle(pending),
                vendor: vendorName(pending?.system ?? ""),
              })}
        </p>
        {pending !== null && (pending.system === "xero" || pending.system === "lucibook") ? (
          <p>{t("connections.revoke_confirm_forced_refresh_caveat")}</p>
        ) : null}
      </ConfirmDialog>
      {/* Round 7 (Opus verification review of round 6, LOW): "Mark as revoked" used to fire with no
          confirmation at all - unlike every other destructive action here - and its old label ("Remove
          from list") already claimed something untrue (the row never actually leaves the list: the web
          fetches `include_revoked: true`). This confirms first, and says truthfully that no vendor call
          is made.

          T4 revoke r7 LOW: the body used to say "there are no stored credentials left" UNCONDITIONALLY -
          true for a row with a genuinely empty blob, but false for one whose blob EXISTS and merely cannot
          be decrypted with the key running now (`revoke_blob_undecryptable`, `ConnectionOut`'s own field).
          Both make no vendor call, but only the first has actually lost its credentials. */}
      <ConfirmDialog
        open={pendingMarkRevoked !== null}
        destructive
        title={t("connections.mark_revoked_confirm_title")}
        confirmLabel={t("connections.mark_revoked")}
        onCancel={() => setPendingMarkRevoked(null)}
        onConfirm={() => {
          const target = pendingMarkRevoked;
          setPendingMarkRevoked(null);
          if (target !== null) revoke.mutate(target.id);
        }}
      >
        <p>
          {pendingMarkRevoked?.revoke_blob_undecryptable
            ? t("connections.mark_revoked_confirm_body_blob_undecryptable", {
                name: pendingMarkRevoked === null ? "" : connectionTitle(pendingMarkRevoked),
                vendor: vendorName(pendingMarkRevoked?.system ?? ""),
              })
            : t("connections.mark_revoked_confirm_body", {
                name: pendingMarkRevoked === null ? "" : connectionTitle(pendingMarkRevoked),
                vendor: vendorName(pendingMarkRevoked?.system ?? ""),
              })}
        </p>
      </ConfirmDialog>
    </Page>
  );
}

/**
 * What a person needs to know about a connection is whether it will keep working, and that is the REFRESH token's
 * business: the access token lives 30 minutes and is renewed on next use, so an expired one is normal for any idle
 * connection. The SERVER owns the verdict (`reconnect_needed` / `disconnected`: a refused refresh, `invalid_grant`, a
 * vendor-side disconnect, or its own check against the rolled-forward refresh expiry). It is trusted as sent: a
 * browser clock a few minutes ahead must not turn a healthy row red.
 */
export function effectiveStatus(row: Connection): "connected" | "reconnect_needed" | "disconnected" {
  return row.status ?? "connected";
}

/** A row a later reconnect replaced: history, with no actions. */
function isSuperseded(row: Connection): boolean {
  return row.superseded_by_id !== null && row.superseded_by_id !== undefined;
}

/** True when another current row of the same system, organisation AND owner is live: this row's "Reconnect" would
 * only create a second connection next to it. FreeAgent never matches: its `org_id` is the signed-in user's email,
 * not a company, so equal values can be different companies. */
export function hasLiveSibling(row: Connection, rows: readonly Connection[]): boolean {
  if (row.system === "freeagent") return false;
  return rows.some(
    (other) =>
      other.id !== row.id &&
      other.system === row.system &&
      other.org_id === row.org_id &&
      (other.owner_id ?? null) === (row.owner_id ?? null) &&
      !isSuperseded(other) &&
      (other.revoke_state ?? "active") === "active" &&
      effectiveStatus(other) === "connected",
  );
}

function connectionColumns({
  rows,
  onRevoke,
  onMarkRevoked,
}: {
  rows: readonly Connection[];
  onRevoke: (row: Connection) => void;
  onMarkRevoked: (row: Connection) => void;
}): readonly Column<Connection>[] {
  return [
    {
      key: "system",
      header: t("connections.col_system"),
      render: (row) => (
        <span className="cellvendor">
          <VendorMark system={row.system} size={28} />
          <span className="cellvendor__name">{vendorName(row.system)}</span>
        </span>
      ),
    },
    {
      key: "organisation",
      header: t("connections.col_organisation"),
      render: (row) => connectionTitle(row),
    },
    {
      key: "status",
      header: t("connections.col_status"),
      render: (row) => {
        const revokeState = (row.revoke_state ?? "active") as RevokeState;
        const status = effectiveStatus(row);
        if (isSuperseded(row)) {
          return (
            <>
              <Badge tone="neutral" dot>
                {t("connections.status_superseded")}
              </Badge>
              <p className="cellnote">{t("connections.superseded_note")}</p>
            </>
          );
        }
        const tone =
          revokeState === "revoke_failed" ||
          row.revoke_warning ||
          status === "reconnect_needed" ||
          status === "disconnected"
            ? ("warn" as const)
            : revokeState === "revoked"
              ? ("neutral" as const)
              : ("pass" as const);
        return (
          <>
            <Badge tone={tone} dot>
              {status === "disconnected"
                ? // XMG-SEC C1: the vendor took it away (found by LuciMigration, not revoked here).
                  t("connections.status_disconnected", { vendor: vendorName(row.system) })
                : revokeState === "active"
                  ? status === "connected"
                    ? t("connections.status_active")
                    : tDynamic("connections.status.", status)
                  : revokeState === "revoke_failed"
                    ? t("revoke.revoke_failed_short")
                    : tDynamic("revoke.", revokeState, { vendor: vendorName(row.system) })}
            </Badge>
            <ConnectionNote row={row} />
          </>
        );
      },
    },
    {
      key: "expires",
      header: t("connections.col_expires"),
      render: (row) => {
        if (isSuperseded(row) || (row.revoke_state ?? "active") !== "active") {
          return <span className="mono muted">—</span>;
        }
        if (effectiveStatus(row) !== "connected") {
          return <span className="mono numfail">{t("connections.needs_reconnect")}</span>;
        }
        // The access-token expiry is only ever detail: it renews on use, so it never alarms.
        const access =
          row.token_expires_at === null || row.token_expires_at === undefined
            ? undefined
            : t("connections.access_token_tooltip", {
                when: formatRelativeExpiry(row.token_expires_at).text,
              });
        return (
          <span className="mono" title={access} data-live>
            {t("connections.renews_automatically")}
          </span>
        );
      },
    },
    {
      key: "refresh",
      header: t("connections.col_refresh_expires"),
      render: (row) => {
        const date = formatDate(row.refresh_expires_at);
        // LuciBook's refresh token lapses after a stretch of INACTIVITY and every refresh mints a new one: with
        // no date from the vendor it is not "unknown", it renews whenever it is used.
        const text =
          date !== ""
            ? date
            : row.system === "lucibook" && !isSuperseded(row)
              ? t("connections.refresh_renews_on_use")
              : "—";
        return <span className="mono muted">{text}</span>;
      },
    },
    {
      key: "actions",
      header: t("connections.col_actions"),
      hiddenHeader: true,
      render: (row) => (
        <ConnectionActions
          row={row}
          canReconnect={!isSuperseded(row) && !hasLiveSibling(row, rows)}
          onRevoke={onRevoke}
          onMarkRevoked={onMarkRevoked}
        />
      ),
    },
  ];
}

/**
 * The per-row note keeps the truthful revoke copy the redesign's table has no column for: the permanent
 * `revoke_failed` case (heading + what to do) and any `revoke_warning` on a revoked row. The retryable case
 * is covered by the banner above the table instead, so its row does not repeat it.
 */
function ConnectionNote({ row }: { row: Connection }): JSX.Element | null {
  const revokeState = (row.revoke_state ?? "active") as RevokeState;
  if (revokeState === "revoke_failed" && !row.revoke_retryable) {
    return (
      <p className="cellnote">
        <strong>{t("revoke.revoke_failed", { vendor: vendorName(row.system) })}</strong>{" "}
        {row.revoke_warning !== undefined && row.revoke_warning !== null && row.revoke_warning !== ""
          ? row.revoke_warning
          : // Fallback only: `_to_out` always sets `revoke_warning` for this shape server-side, so this is
            // unreached in practice - kept for a row served by a stale/cached response.
            t("connections.revoke_failed_unretryable_detail", {
              vendor: vendorName(row.system),
              holder: revokeAccessHolderName(row.system),
            })}
      </p>
    );
  }
  if (
    revokeState === "revoked" &&
    row.revoke_warning !== undefined &&
    row.revoke_warning !== null &&
    row.revoke_warning !== ""
  ) {
    return <p className="cellnote">{row.revoke_warning}</p>;
  }
  return null;
}

function ConnectionActions({
  row,
  canReconnect,
  onRevoke,
  onMarkRevoked,
}: {
  row: Connection;
  /** False on a superseded row and on one whose organisation already has a live connection. */
  canReconnect: boolean;
  onRevoke: (row: Connection) => void;
  onMarkRevoked: (row: Connection) => void;
}): JSX.Element {
  const revokeState = (row.revoke_state ?? "active") as RevokeState;
  const status = effectiveStatus(row);
  // History has no actions: the row is kept for the migrations and audit entries that point at it.
  if (isSuperseded(row)) return <span className="cellactions" />;
  const reconnect = !canReconnect ? null : row.system === "lucibook" ? (
    <LuciBookReconnectButton />
  ) : row.system === "xero" ? (
    // XMG-SEC C1: a Xero row disconnected on Xero's side (or revoked here) has a way back.
    <XeroReconnectButton />
  ) : null;
  return (
    <span className="cellactions">
      {revokeState === "revoked" || (revokeState === "revoke_failed" && !row.revoke_retryable) ? (
        revokeState === "revoked" || row.system === "lucibook" ? (
          reconnect
        ) : (
          // Round 6 (Opus verification review of round 5, LOW): a legacy or PERMANENT `revoke_failed` row
          // with no stored credentials used to be a dead end here - no button at all, so it stayed in the
          // default list forever even though `DELETE /connections/{id}` already handles it correctly with
          // no blob to send. This offers that SAME terminal action explicitly.
          <Button size="compact" onClick={() => onMarkRevoked(row)}>
            {t("connections.mark_revoked")}
          </Button>
        )
      ) : (
        <>
          {status === "reconnect_needed" ? reconnect : null}
          {revokeState === "revoke_failed" ? (
            <Button size="compact" onClick={() => onRevoke(row)}>
              {t("connections.revoke_again")}
            </Button>
          ) : (
            <Button size="compact" variant="destructive" onClick={() => onRevoke(row)}>
              {t("connections.revoke")}
            </Button>
          )}
        </>
      )}
    </span>
  );
}

/**
 * The "Reconnect needed" affordance (2026-09-24 owner decision, task 5): a revoked LuciBook row — a real
 * revocation, OR a pre-decision `client_credentials` "custom connection" row the landing data migration
 * marked `revoked_at`-set because that credential shape no longer authenticates anything (`invalid_grant`
 * on the next refresh attempt would have surfaced the same way, this just says so up front) — gets a
 * button that restarts the SAME `start_lucibook_authorization` → `window.location.assign` handoff
 * `AddConnectionScreen`'s tile uses, not a dead row with no way back.
 */
function LuciBookReconnectButton(): JSX.Element {
  const authorize = useStartLuciBookAuthorization();
  const toast = useToast();

  async function onReconnect(): Promise<void> {
    try {
      const authorization = await authorize.mutateAsync({});
      window.location.assign(authorization.authorization_url);
    } catch (caught) {
      toast.push(
        describeApiError(caught, t("error.unexpected")),
        isApiError(caught) ? caught.requestId : null,
        "error",
      );
    }
  }

  return (
    <Button
      size="compact"
      variant="primary"
      onClick={() => void onReconnect()}
      disabled={authorize.isPending}
    >
      {t("connections.lucibook_reconnect")}
    </Button>
  );
}

/**
 * XMG-SEC C1: the Xero counterpart of `LuciBookReconnectButton`. Xero has no disconnect webhook, so the API finds a
 * Xero-side disconnect itself and serves the row as `disconnected`; this restarts the SAME `start_xero_authorization`
 * handoff `AddConnectionScreen`'s Xero tile uses (the draft's `return_to` brings the person back here). The web connect
 * creates a NEW connection row; the disconnected row stays as the record of what happened.
 */
function XeroReconnectButton(): JSX.Element {
  const authorize = useStartXeroAuthorization();
  const toast = useToast();

  async function onReconnect(): Promise<void> {
    try {
      const authorization = await authorize.mutateAsync({});
      // Written before the navigation, because the navigation destroys this page (§B1.5).
      saveDraft({ return_to: "/connections" });
      window.location.assign(authorization.authorization_url);
    } catch (caught) {
      toast.push(
        describeApiError(caught, t("error.unexpected")),
        isApiError(caught) ? caught.requestId : null,
        "error",
      );
    }
  }

  return (
    <Button
      size="compact"
      variant="primary"
      onClick={() => void onReconnect()}
      disabled={authorize.isPending}
    >
      {t("connections.xero_reconnect")}
    </Button>
  );
}
